// Global packages and components
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'
import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid'

// Types
interface Props {
  value: string
  disabled?: boolean
  min?: number
  max?: number
  errorMessage?: string
  errorAbove?: boolean
  compact?: boolean
  setValue: (e: string) => void
  onChange?: (e: string) => void
}

// Main export
const QuantitySelector = ({
  value,
  disabled,
  min = 0,
  max,
  errorMessage,
  errorAbove,
  compact,
  setValue,
  onChange,
}: Props) => {
  const [error, setError] = useState<string | null>(errorMessage ?? null)

  // Checks if the value hits any restriction boundaries we have set
  const checkHasError = (value: number): string | false => {
    // Rstrictions exist
    const hasMin = typeof min === 'number'
    const hasMax = typeof max === 'number'

    // Restrictions are not met
    const minValid = !hasMin || value >= min
    const maxValid = !hasMax || value <= max

    // Return error
    if (!minValid || isNaN(value)) {
      return 'min'
    } else if (!maxValid) {
      return 'max'
    } else {
      return false
    }
  }

  // Set the error message
  const handleError = (type: string) => {
    setError(type == 'min' ? `Min ${min} items` : `Max ${max} items`)
  }

  // User types into input field
  const handleOnChange = (value: string) => {
    setError(null) // Clear current error

    // Allow empty string or just numbers
    if (value.trim() === '' || /^\d+$/.test(value)) {
      setValue(value)
    }
  }

  // User clicks off the input field
  const handleOnBlur = () => {
    const valNum = Number(value)
    const hasError = checkHasError(valNum)

    if (hasError) {
      // Check for error
      handleError(hasError)
    } else if (onChange) {
      // Fire on change event
      onChange(value)
    }
  }

  const handleIncrement = (type: string) => {
    setError(null) // Clear current error

    const valNum = type == 'minus' ? Number(value) - 1 : Number(value) + 1
    const hasError = checkHasError(valNum)

    if (hasError) {
      handleError(hasError) // Set error
    } else {
      setValue(String(valNum)) // Set value

      if (onChange) {
        onChange(String(valNum)) // Fire on change event
      }
    }
  }

  useEffect(() => setError(errorMessage ?? null), [errorMessage])

  return (
    <div>
      {errorAbove && error && (
        <div
          className={clsx('pb-1 text-center', {
            'w-[120px]': compact,
            'w-[145px]': !compact,
          })}
        >
          <p className="mb-0 text-xs text-red-600">{error}</p>
        </div>
      )}
      <div className="flex">
        <button
          disabled={
            disabled || (typeof min === 'number' && Number(value) <= min)
          }
          onClick={() => handleIncrement('minus')}
          className={clsx(
            'flex items-center justify-center rounded-l-lg border-b border-l border-t border-neutral-300 bg-white text-neutral-900 disabled:bg-neutral-150 disabled:text-neutral-500',
            {
              'w-7': compact,
              'w-8': !compact,
            }
          )}
          data-sp-button-label="Minus quantity"
        >
          <MinusIcon className="size-3" />
        </button>
        <input
          type="text"
          inputMode="numeric"
          value={value}
          placeholder=""
          className={clsx(
            'border border-neutral-300 bg-white px-4 py-2 text-center focus:border-teal-green focus:outline-none disabled:bg-neutral-150 disabled:text-neutral-500',
            {
              'w-14 text-sm': compact,
              'w-[4.3rem] md:py-3': !compact,
            }
          )}
          disabled={disabled}
          pattern="[0-9]"
          data-sp-button-label="Quantity"
          onChange={e => {
            handleOnChange(e.target.value)
          }}
          onBlur={handleOnBlur}
          onFocus={() => setError(null)}
        />
        <button
          disabled={
            disabled || (typeof max === 'number' && Number(value) >= max)
          }
          onClick={() => handleIncrement('plus')}
          className={clsx(
            'flex items-center justify-center rounded-r-lg border-b border-r border-t border-neutral-300 bg-white text-neutral-900 disabled:bg-neutral-150 disabled:text-neutral-500',
            {
              'w-7': compact,
              'w-8': !compact,
            }
          )}
          data-sp-button-label="Add quantity"
        >
          <PlusIcon className="size-4" />
        </button>
      </div>
      {!errorAbove && error && (
        <div
          className={clsx('pt-1 text-center', {
            'w-[112px]': compact,
            'w-[145px]': !compact,
          })}
        >
          <p className="mb-0 text-xs text-red-600">{error}</p>
        </div>
      )}
    </div>
  )
}

export default QuantitySelector
